import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, User, Lock, Store } from 'lucide-react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { useLogin } from '@/hooks/useAuth';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { PlatformFooterMessage, PartnershipValueProps } from '@/components/common/PartnershipBranding';
import { LogoIcon } from '@/assets/logo';
import { cn } from '@/utils';
import {
  composeSubmittedLogin,
  forgetRestaurant,
  getRememberedRestaurant,
  type RememberedRestaurant,
} from '@/lib/rememberedRestaurant';

/*
 * No format validation on the login field.
 *
 * It used to be `.email()`, which is exactly what made a login have to look
 * like an address. A login is `name@restaurantCode` — and on a device that
 * remembers its restaurant, just `aigul`. Neither is an email, and any shape
 * rule here would reject somebody's real login rather than let the server say
 * whether the credential is right.
 */
const createLoginSchema = (t: any) => z.object({
  login: z
    .string()
    .min(1, t('errors.validationError')),
  password: z
    .string()
    .min(1, t('errors.validationError'))
    .min(6, t('errors.validationError')),
  // .default() makes the schema's INPUT type differ from its output, which
  // left useForm inferring every field as optional and the submit handler
  // receiving a partial. optional() keeps input and output aligned.
  rememberMe: z.boolean().optional(),
});

/*
 * Use the shared LoginForm rather than inferring a second one from the schema.
 *
 * There were two types with this name: this local z.infer and the one in
 * @/types that the auth store and useLogin speak. They disagreed — the inferred
 * one came out with every field optional — so the submit handler could not be
 * passed to the mutation.
 */
import type { LoginForm } from '@/types';

// Demo accounts removed - using real backend authentication only

export const LoginPage: React.FC = () => {
  const { t } = useSafeTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const loginMutation = useLogin();

  /*
   * The restaurant this device signs into, learned from the last successful
   * sign-in. When it is known the form asks for a short name and shows the
   * restaurant by name; when it is not, it asks for the whole login.
   */
  const [remembered, setRemembered] = useState<RememberedRestaurant | null>(() =>
    getRememberedRestaurant()
  );

  const from = location.state?.from?.pathname || '/';

  const loginSchema = createLoginSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<LoginForm>({
    // Pinned: the schema is built inside a factory taking `t: any`, so its
    // inferred shape does not line up with LoginForm on its own and useForm
    // fell back to treating every field as optional.
    resolver: zodResolver(loginSchema) as Resolver<LoginForm>,
    defaultValues: {
      login: '',
      password: '',
      rememberMe: false,
    },
  });

  /** Drop the remembered restaurant and ask for a full login again. */
  const useAnotherRestaurant = () => {
    forgetRestaurant();
    setRemembered(null);
    setFocus('login');
  };

  const onSubmit = async (data: LoginForm) => {
    try {
      // A typed value already containing '@' is taken as written — that is how
      // somebody signs into a different restaurant from a device that
      // remembers one.
      await loginMutation.mutateAsync({
        ...data,
        login: composeSubmittedLogin(data.login, remembered?.code ?? null),
      });
      navigate(from, { replace: true });
    } catch (error) {
      // Error is handled in the mutation with toast
    }
  };

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  // handleDemoLogin removed - real authentication only

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Language Switcher */}
      <div className="absolute top-6 right-6">
        <LanguageSwitcher variant="floating" />
      </div>
      
      {/* Nova Cafe Brand Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex justify-center mb-5"
        >
          <LogoIcon size={56} className="text-neutral-900" />
        </motion.div>

        <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 mb-2">
          {t('auth.brandName')}
        </h1>
        {/* auth.brandTagline said the same thing as auth.loginSubtitle
            ("Платформа управления рестораном"), so only the subtitle remains. */}
        <p className="text-neutral-500">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      {/* Login Form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 bg-white border border-neutral-200 rounded-2xl shadow-sm p-6 sm:p-8"
      >
        {/* The restaurant this device belongs to, when it is known. Shown as a
            statement rather than a field: it is not something the person types,
            and it is the context that makes a bare short name meaningful.

            Stacked on a narrow screen: side by side, the switch link squeezed
            the restaurant name down to "Bella Vista C…" on a phone — which is
            the device most of these people sign in on, and the name is the one
            thing this row exists to state. */}
        {remembered && (
          <div className="flex flex-col gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <Store className="h-4 w-4 shrink-0 text-neutral-500" />
              <span className="truncate text-sm font-medium text-neutral-800">
                {remembered.name}
              </span>
            </span>
            <button
              type="button"
              onClick={useAnotherRestaurant}
              className="shrink-0 self-start text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline underline-offset-4 sm:self-auto"
            >
              {t('auth.changeRestaurant')}
            </button>
          </div>
        )}

        {/* Login Field */}
        <div>
          <label htmlFor="login" className="block text-sm font-medium text-neutral-700 mb-2">
            {remembered ? t('auth.loginNameLabel') : t('auth.loginLabel')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              {...register('login')}
              // Deliberately `text`, not `email`: type="email" makes the
              // browser refuse to submit `aigul` and demand an address, which
              // is the exact constraint this whole change removes.
              type="text"
              id="login"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={remembered ? t('auth.loginNamePlaceholder') : t('auth.loginPlaceholder')}
              className={cn(
                'w-full pl-10 pr-4 py-2.5 rounded-lg border bg-white text-neutral-900 placeholder-neutral-400 shadow-xs transition-colors duration-150',
                'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none',
                errors.login
                  ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500/20'
                  : 'border-neutral-300'
              )}
            />
          </div>
          {errors.login ? (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-danger-600"
            >
              {errors.login.message}
            </motion.p>
          ) : (
            <p className="mt-2 text-xs text-neutral-500">
              {remembered
                ? t('auth.loginNameHint', { restaurant: remembered.name })
                : t('auth.loginHint')}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
            {t('auth.password')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              placeholder={t('auth.password')}
              className={cn(
                'w-full pl-10 pr-12 py-2.5 rounded-lg border bg-white text-neutral-900 placeholder-neutral-400 shadow-xs transition-colors duration-150',
                'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none',
                errors.password
                  ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500/20'
                  : 'border-neutral-300'
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              // min-w-44: the glyph is 20px and pr-3 made the whole control
              // 32px wide — under the 44px tap minimum on its short side.
              className="absolute inset-y-0 right-0 flex items-center justify-center min-w-[44px]"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
              ) : (
                <Eye className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
              )}
            </button>
          </div>
          {errors.password && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-danger-600"
            >
              {errors.password.message}
            </motion.p>
          )}
        </div>

        {/* Remember Me */}
        <div className="flex items-center">
          <label className="flex items-center">
            <input
              {...register('rememberMe')}
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <span className="ml-2 text-sm text-neutral-600">{t('auth.rememberMe')}</span>
          </label>
        </div>

        {/*
          Where «Забыли пароль?» used to be.

          That link opened nothing — it was wired to a client method calling an
          endpoint the backend has never had. A self-service reset is delivered
          by email, and email is not a channel anyone here reads, so it was
          never going to exist. Recovery is a person: staff ask whoever hired
          them, an owner asks Nova. Saying so is more useful than a link that
          cannot work.
        */}
        <p className="rounded-lg bg-neutral-50 px-3 py-2.5 text-xs leading-relaxed text-neutral-600">
          {t('auth.passwordHelp')}
        </p>

        {/* Login Button */}
        <Button
          type="submit"
          fullWidth
          loading={loginMutation.isPending}
          leftIcon={!loginMutation.isPending ? <LogIn className="h-5 w-5" /> : undefined}
          size="lg"
        >
          {loginMutation.isPending ? t('common.loading') : t('auth.loginButton')}
        </Button>
      </form>

      {/* Demo accounts removed - real authentication only */}

      {/* Footer — every line centred and spaced on one rhythm.
          footer.description and auth.footerTagline both restated the header
          subtitle, so the block is down to the network line plus copyright. */}
      <div className="mt-8 flex flex-col items-center gap-2 text-center">
        <PlatformFooterMessage />
        <p className="text-xs text-neutral-400">
          {/* Year computed at render, not baked into the string — the notice
              read "© 2024" well into 2026. */}
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </motion.div>
  );
};